(() => {
  if (customElements.get('dac-studio-gallery')) return;
  class DACStudioGallery extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.buttons = Array.from(this.querySelectorAll('[data-select]'));
      this.main = this.querySelector('[data-main-photo]');
      this.stage = this.querySelector('.dac-photo-stage');
      this.images = new Map();
      this.photoVisible = false;
      this.index = Math.max(0, this.buttons.findIndex(button => button.getAttribute('aria-pressed') === 'true'));
      this.targetIndex = this.index;
      this.requestId = (this.requestId || 0) + 1;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = matchMedia('(max-width: 767px)');
      this.paused = this.reduced.matches;
      this.inView = true;
      this.querySelectorAll('[hidden]').forEach(el => el.hidden = false);
      this.classList.add('is-ready');
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button || !this.contains(button)) return;
        if (button.matches('[data-select]')) this.select(Number(button.dataset.select));
        else if (button.matches('[data-prev]')) this.select(this.targetIndex - 1);
        else if (button.matches('[data-next]')) this.select(this.targetIndex + 1);
        else if (button.matches('.dac-scroll-control')) {
          this.paused = !this.paused;
          this.updateMotion();
        }
      }, { signal });
      this.addEventListener('pointerover', event => this.prepareIntent(event.target), { signal, passive:true });
      this.addEventListener('focusin', event => this.prepareIntent(event.target), { signal });
      this.stage.addEventListener('pointerdown', event => {
        if (this.rail) return;
        if (!event.isPrimary || event.button !== 0) { this.swipe = null; return; }
        this.swipe = { id:event.pointerId, x:event.clientX, y:event.clientY, intent:0, width:this.stage.clientWidth };
        this.stage.setPointerCapture(event.pointerId);
      }, { signal, passive:true });
      this.stage.addEventListener('pointermove', event => {
        if (!this.swipe || event.pointerId !== this.swipe.id) return;
        const dx=event.clientX-this.swipe.x,dy=event.clientY-this.swipe.y;
        if (Math.abs(dx)>12 && Math.abs(dx)>Math.abs(dy)*1.3) {
          const direction=dx<0?1:-1;
          if (this.swipe.intent!==direction) {
            this.swipe.intent=direction;
            this.loadPhoto(this.targetIndex+direction,'low').catch(()=>{});
          }
        }
      }, { signal, passive:true });
      this.stage.addEventListener('pointerup', event => {
        const swipe=this.swipe;
        this.swipe=null;
        if (!swipe || event.pointerId!==swipe.id) return;
        if (this.stage.hasPointerCapture(event.pointerId)) this.stage.releasePointerCapture(event.pointerId);
        const dx=event.clientX-swipe.x,dy=event.clientY-swipe.y;
        if (Math.abs(dx)>=Math.max(36,swipe.width*.1) && Math.abs(dx)>Math.abs(dy)*1.3) this.select(this.targetIndex+(dx<0?1:-1));
      }, { signal, passive:true });
      this.stage.addEventListener('pointercancel', ()=>{this.swipe=null;}, { signal, passive:true });
      this.reduced.addEventListener('change', event => {
        this.paused = event.matches;
        this.updateMotion();
      }, { signal });
      document.addEventListener('visibilitychange', () => this.updateMotion(), { signal });
      this.observer = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting;
        this.updateMotion();
      });
      this.observer.observe(this.querySelector('.dac-marquee'));
      this.photoObserver = new IntersectionObserver(entries=>{
        this.photoVisible=entries[0].isIntersecting;
        if (this.photoVisible) this.queueNextPhoto(); else this.cancelPreload();
      }, { threshold:.15 });
      this.photoObserver.observe(this.stage);
      this.main.addEventListener('load',()=>this.queueNextPhoto(),{signal});
      this.resizeObserver = new ResizeObserver(()=>{
        this.main.sizes='auto, '+this.photoSizes(this.index);
        if (this.rail && this.railWidth !== this.rail.clientWidth) {
          this.railWidth = this.rail.clientWidth;
          this.rail.scrollTo({left:this.index*this.railWidth,behavior:'instant'});
          this.sizeRail();
        }
      });
      this.resizeObserver.observe(this.stage);
      this.mobile.addEventListener('change',()=>this.configureMobile(),{signal});
      this.configureMobile();
      this.updateMotion();
    }

    photoSizes(index) {
      const photo=this.buttons[index].dataset;
      const fit=Math.min(1,Number(photo.aspect));
      const width=this.mobile.matches?(this.stage.clientWidth-4)*fit:(this.stage.clientWidth*fit-4);
      return Math.max(1,Math.ceil(width*.97))+'px';
    }

    configureMobile() {
      if (this.mobile.matches === Boolean(this.rail)) return;
      ++this.requestId;
      this.swipe = null;
      this.programmaticIndex=null;
      this.draggingRail=false;
      this.targetIndex = this.index;
      if (!this.mobile.matches) {
        clearTimeout(this.settleTimer);
        this.railController?.abort();
        const aura=this.slides[this.index].querySelector('.dac-aura');
        this.rail.replaceWith(aura);
        this.rail=null;
        this.slides=null;
        const photo=this.buttons[this.index].dataset;
        aura.style.width=100*Math.min(1,Number(photo.aspect))+'%';
        aura.querySelector('.dac-photo-well').style.aspectRatio=photo.aspect;
        this.main.sizes='auto, '+this.photoSizes(this.index);
        return;
      }
      const template=this.stage.querySelector('.dac-aura');
      const frameTemplate=template.cloneNode(true);
      this.railController=new AbortController();
      const {signal}=this.railController;
      this.rail=document.createElement('div');
      this.rail.className='dac-mobile-rail';
      this.rail.setAttribute('aria-label','Artwork photos. Swipe left or right to explore.');
      this.rail.setAttribute('role','region');
      this.rail.tabIndex=0;
      this.slides=this.buttons.map((button,index)=>{
        const slide=document.createElement('div'),photo=button.dataset;
        slide.className='dac-mobile-slide';
        slide.setAttribute('role','group');
        slide.setAttribute('aria-label',`${index+1} of ${this.buttons.length}: ${photo.title}`);
        slide.setAttribute('aria-hidden',String(index!==this.index));
        const aura=frameTemplate.cloneNode(true);
        aura.style.width='100%';
        aura.style.setProperty('--dac-glow',photo.glow);
        const well=aura.querySelector('.dac-photo-well'),aspect=Number(photo.aspect);
        well.style.aspectRatio='1';
        well.style.setProperty('--dac-photo-width',97*Math.min(1,aspect)+'%');
        well.style.setProperty('--dac-photo-height',97/Math.max(1,aspect)+'%');
        const img=aura.querySelector('[data-main-photo]');
        img.removeAttribute('data-main-photo');
        img.alt=photo.alt;
        img.width=Number(photo.width);img.height=Number(photo.height);
        if (index===this.index) img.replaceWith(this.main);
        else {
          img.removeAttribute('srcset');img.removeAttribute('sizes');
          img.src=button.querySelector('img').src;
        }
        slide.append(aura);
        this.rail.append(slide);
        return slide;
      });
      template.replaceWith(this.rail);
      this.railWidth=this.rail.clientWidth;
      this.rail.scrollLeft=this.index*this.railWidth;
      this.sizeRail();
      this.rail.addEventListener('scroll',()=>{
        const nearest=Math.max(0,Math.min(this.buttons.length-1,Math.round(this.rail.scrollLeft/this.rail.clientWidth)));
        if (nearest!==this.index) this.prepareSlide(nearest).catch(()=>{});
        clearTimeout(this.settleTimer);
        // Fallback for browsers without scrollend; captions change only after snapping.
        this.settleTimer=setTimeout(()=>this.settleRail(),140);
      },{signal,passive:true});
      this.rail.addEventListener('scrollend',()=>this.settleRail(),{signal});
      this.rail.addEventListener('pointerdown',()=>{
        // A new touch takes ownership from any pending arrow/thumbnail navigation.
        ++this.requestId;
        this.programmaticIndex=null;
        this.targetIndex=this.index;
      },{signal,passive:true});
      this.rail.addEventListener('touchstart',()=>{this.draggingRail=true;},{signal,passive:true});
      const finishTouch=()=>{
        this.draggingRail=false;
        clearTimeout(this.settleTimer);
        this.settleTimer=setTimeout(()=>this.settleRail(),140);
      };
      this.rail.addEventListener('touchend',finishTouch,{signal,passive:true});
      this.rail.addEventListener('touchcancel',finishTouch,{signal,passive:true});
      this.rail.addEventListener('keydown',event=>{
        if (event.key!=='ArrowRight' && event.key!=='ArrowLeft') return;
        event.preventDefault();
        this.select(this.targetIndex+(event.key==='ArrowRight'?1:-1));
      },{signal});
      this.queueNextPhoto();
    }

    sizeRail() {
      if (!this.rail) return;
      // Reserve one square window for every artwork, independent of its proportions.
      this.rail.style.height=(this.stage.clientWidth+24)+'px';
    }

    async prepareSlide(index,priority='auto') {
      const rail=this.rail,slide=this.slides?.[index];
      const image=await this.loadPhoto(index,priority);
      if (!rail || rail!==this.rail || !slide) return;
      const img=slide.querySelector('.dac-photo-well img'),photo=this.buttons[index].dataset;
      img.sizes=this.photoSizes(index);
      img.srcset=photo.srcset;
      img.src=photo.src;
      // decode() also covers installing a previously cached response into this element.
      await img.decode();
      return image;
    }

    async settleRail() {
      clearTimeout(this.settleTimer);
      if (!this.rail || this.draggingRail) return;
      const position=this.rail.scrollLeft/this.rail.clientWidth;
      const index=Math.max(0,Math.min(this.buttons.length-1,Math.round(position)));
      if (this.programmaticIndex!=null && index!==this.programmaticIndex) {
        if (this.navigationReady) this.rail.scrollTo({left:this.programmaticIndex*this.rail.clientWidth,behavior:this.reduced.matches?'instant':'smooth'});
        return;
      }
      if (Math.abs(position-index)>.02) {
        // Interrupting a smooth scroll can leave some engines between snap points.
        this.rail.scrollTo({left:index*this.rail.clientWidth,behavior:this.reduced.matches?'instant':'smooth'});
        return;
      }
      if (index===this.index) { this.programmaticIndex=null;this.targetIndex=index;return; }
      const token=++this.requestId,rail=this.rail;
      try { await this.prepareSlide(index); }
      catch (_) {
        if (token===this.requestId && rail===this.rail) {
          this.targetIndex=this.index;
          this.programmaticIndex=null;
          rail.scrollTo({left:this.index*rail.clientWidth,behavior:this.reduced.matches?'instant':'smooth'});
        }
        return;
      }
      if (token!==this.requestId || rail!==this.rail || !this.isConnected) return;
      this.main.removeAttribute('data-main-photo');
      this.main=this.slides[index].querySelector('.dac-photo-well img');
      this.main.setAttribute('data-main-photo','');
      this.index=this.targetIndex=index;
      this.programmaticIndex=null;
      this.updateCaption();
      this.slides.forEach((slide,i)=>{
        slide.setAttribute('aria-hidden',String(i!==index));
        // Keep high-resolution DOM images close to the selected photograph.
        if (Math.abs(i-index)>1) {
          const img=slide.querySelector('.dac-photo-well img');
          img.removeAttribute('srcset');img.removeAttribute('sizes');
          img.src=this.buttons[i].querySelector('img').src;
        }
      });
      this.queueNextPhoto();
    }

    updateCaption() {
      const photo=this.buttons[this.index].dataset;
      this.style.setProperty('--dac-glow',photo.glow);
      this.querySelector('[data-photo-title]').textContent=photo.title;
      this.querySelector('[data-photo-artist]').textContent=photo.artist;
      this.querySelector('[data-product-link]').href=photo.url;
      this.querySelector('.dac-counter').textContent=String(this.index+1).padStart(2,'0')+' / '+this.buttons.length;
      this.buttons.forEach((button,i)=>button.setAttribute('aria-pressed',String(i===this.index)));
    }

    loadPhoto(index,priority='auto') {
      index=(index+this.buttons.length)%this.buttons.length;
      const photo=this.buttons[index].dataset,sizes=this.photoSizes(index),key=index+'|'+sizes+'|'+devicePixelRatio;
      if (this.images.has(key)) {
        const cached=this.images.get(key);
        if (priority!=='low') cached.image.fetchPriority=priority;
        return cached.ready;
      }
      const image=new Image();
      image.decoding='async';
      image.fetchPriority=priority;
      image.sizes=sizes;
      image.srcset=photo.srcset;
      image.src=photo.src;
      const ready=image.decode().then(()=>image).catch(error=>{this.images.delete(key);throw error;});
      this.images.set(key,{image,ready});
      // Keep a few nearby decoded images, rather than the entire gallery in memory.
      while (this.images.size>4) this.images.delete(this.images.keys().next().value);
      return ready;
    }

    prepareIntent(target) {
      const button=target.closest('button');
      if (!button || !this.contains(button)) return;
      let index;
      if (button.matches('[data-select]')) index=Number(button.dataset.select);
      else if (button.matches('[data-next]')) index=this.targetIndex+1;
      else if (button.matches('[data-prev]')) index=this.targetIndex-1;
      if (index!==undefined) this.loadPhoto(index,'low').catch(()=>{});
    }

    cancelPreload() {
      if (this.idleRequest!==undefined) {
        if ('cancelIdleCallback' in window) cancelIdleCallback(this.idleRequest);
        else clearTimeout(this.idleRequest);
        this.idleRequest=undefined;
      }
    }

    queueNextPhoto() {
      this.cancelPreload();
      const connection=navigator.connection;
      if (!this.photoVisible || !this.main.complete || !this.main.naturalWidth || document.hidden || connection?.saveData || /(^|-)2g|3g/.test(connection?.effectiveType || '')) return;
      const load=()=>{
        this.idleRequest=undefined;
        if (this.photoVisible && this.isConnected && !document.hidden) {
          const next=(this.index+1)%this.buttons.length;
          (this.rail?this.prepareSlide(next,'low'):this.loadPhoto(next,'low')).catch(()=>{});
        }
      };
      this.idleRequest='requestIdleCallback' in window ? requestIdleCallback(load,{timeout:1500}) : setTimeout(load,500);
    }

    updateMotion() {
      const moving = !this.paused && this.inView && !document.hidden;
      this.dataset.moving = String(moving);
      const control = this.querySelector('.dac-scroll-control');
      control.setAttribute('aria-label', this.paused ? 'Play scrolling text' : 'Pause scrolling text');
      // The icon expresses the user's setting, including when the band is offscreen.
      control.querySelector('[data-scroll-icon="pause"]').hidden = this.paused;
      control.querySelector('[data-scroll-icon="play"]').hidden = !this.paused;
    }

    async select(index) {
      index = (index + this.buttons.length) % this.buttons.length;
      this.targetIndex = index;
      if (this.rail) { this.programmaticIndex=index;this.navigationReady=false; }
      const token = ++this.requestId;
      const photo = this.buttons[index].dataset;
      try {
        if (this.rail) await this.prepareSlide(index);
        else await this.loadPhoto(index);
      } catch (_) {
        // Keep the last successful photograph if a request fails.
        if (token === this.requestId) { this.targetIndex = this.index;this.programmaticIndex=null; }
        return;
      }
      if (token !== this.requestId || !this.isConnected) return;
      if (this.rail) {
        this.navigationReady=true;
        // Adjacent arrows animate like a swipe; distant thumbnails jump directly.
        this.rail.scrollTo({left:index*this.rail.clientWidth,behavior:!this.reduced.matches && Math.abs(index-this.index)===1?'smooth':'instant'});
        clearTimeout(this.settleTimer);
        this.settleTimer=setTimeout(()=>this.settleRail(),180);
        return;
      }
      this.index = index;
      this.querySelector('.dac-aura').style.width = 100 * Math.min(1, Number(photo.aspect)) + '%';
      this.querySelector('.dac-photo-well').style.aspectRatio = photo.aspect;
      this.main.sizes = 'auto, '+this.photoSizes(index);
      this.main.width = Number(photo.width);
      this.main.height = Number(photo.height);
      this.main.srcset = photo.srcset;
      this.main.src = photo.src;
      this.main.alt = photo.alt;
      this.updateCaption();
      this.main.getAnimations().forEach(animation=>animation.cancel());
      if (!this.reduced.matches) this.main.animate([{ opacity: .8 }, { opacity: 1 }], { duration: 160, easing: 'ease-out' });
      this.queueNextPhoto();
    }

    disconnectedCallback() {
      this.requestId++;
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      this.photoObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.railController?.abort();
      clearTimeout(this.settleTimer);
      if (this.rail) {
        this.rail.replaceWith(this.slides[this.index].querySelector('.dac-aura'));
        this.rail=null;this.slides=null;
      }
      this.cancelPreload();
      this.images?.clear();
      this.swipe=null;
    }
  }
  customElements.define('dac-studio-gallery', DACStudioGallery);
})();
